(function (window) {
  'use strict';

  const ASSOCIATION_PROPERTY = '_fly_personalization_group';

  function validationError(message) {
    const error = new Error(message);
    error.name = 'ProductFormValidationError';
    return error;
  }

  function messageFor(messages, key) {
    const localizedMessage = messages?.[key];

    if (typeof localizedMessage === 'string' && localizedMessage.trim() !== '') {
      return localizedMessage;
    }

    return key.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  }

  function toVariantId(variant, messageKey, messages) {
    const id = Number(variant && (variant.id ?? variant));

    if (!Number.isInteger(id) || id < 1) {
      throw validationError(messageFor(messages, messageKey));
    }

    return id;
  }

  function toQuantity(value, messages) {
    const quantity = Number(value ?? 1);

    if (!Number.isInteger(quantity) || quantity < 1) {
      throw validationError(messageFor(messages, 'invalidQuantity'));
    }

    return quantity;
  }

  function isConfirmed(personalization) {
    return Boolean(personalization && (personalization.confirmed === true || personalization.state === 'confirmed'));
  }

  function normalizeProperties(properties) {
    if (!properties) return {};

    if (properties instanceof Map) {
      return Object.fromEntries(properties.entries());
    }

    return { ...properties };
  }

  function addLineItemFields(item, properties, sellingPlan) {
    const normalizedProperties = normalizeProperties(properties);

    if (Object.keys(normalizedProperties).length > 0) {
      item.properties = normalizedProperties;
    }

    if (sellingPlan !== undefined && sellingPlan !== null && String(sellingPlan) !== '') {
      item.selling_plan = sellingPlan;
    }

    return item;
  }

  function requireCompletePersonalization(personalization, messages) {
    const requiredFields = ['initials', 'font', 'colour'];
    const complete = requiredFields.every(
      (field) => personalization && personalization[field] !== undefined && String(personalization[field]).trim() !== '',
    );

    if (!complete) {
      throw validationError(messageFor(messages, 'incompletePersonalization'));
    }
  }

  function requireAssociationId(personalization, explicitId, messages) {
    const candidate = explicitId ?? personalization?.associationId;

    if (candidate === undefined || candidate === null || String(candidate).trim() === '') {
      throw validationError(messageFor(messages, 'missingAssociation'));
    }

    return String(candidate).trim();
  }

  function buildItems(input = {}) {
    const messages = input.messages || {};
    const baseId = toVariantId(input.baseVariant, 'invalidBaseVariant', messages);
    const quantity = toQuantity(input.quantity, messages);
    const personalization = input.personalization || null;
    const required = input.personalizationRequired === true || personalization?.required === true;
    const confirmed = isConfirmed(personalization);
    const baseProperties = input.properties ?? input.lineItemProperties;

    if (required && !confirmed) {
      throw validationError(messageFor(messages, 'requiredPersonalization'));
    }

    if (!confirmed) {
      return [addLineItemFields({ id: baseId, quantity }, baseProperties, input.sellingPlan)];
    }

    requireCompletePersonalization(personalization, messages);

    const surcharge = input.surchargeVariant ?? personalization.surchargeVariant;

    if (!surcharge || surcharge.available !== true) {
      throw validationError(messageFor(messages, 'unavailablePersonalization'));
    }

    const surchargeId = toVariantId(surcharge, 'invalidSurchargeVariant', messages);

    const associationId = requireAssociationId(personalization, input.associationId, messages);
    const associationProperties = {
      [ASSOCIATION_PROPERTY]: associationId,
    };
    const baseItem = addLineItemFields(
      {
        id: baseId,
        quantity,
        properties: {
          ...normalizeProperties(baseProperties),
          ...associationProperties,
        },
      },
      undefined,
      input.sellingPlan,
    );
    const surchargeProperties = {
      Initials: personalization.initials,
      Font: personalization.font,
      Colour: personalization.colour,
      ...((personalization.productTitle !== undefined && { Product: personalization.productTitle }) || {}),
      ...associationProperties,
    };

    return [
      baseItem,
      {
        id: surchargeId,
        quantity,
        parent_id: baseId,
        properties: surchargeProperties,
      },
    ];
  }

  function appendSectionParams(formData, sections, sectionsUrl) {
    formData.delete('sections');
    formData.delete('sections_url');

    if (Array.isArray(sections) ? sections.length > 0 : sections) {
      formData.append('sections', Array.isArray(sections) ? sections.join(',') : String(sections));
    }

    if (sectionsUrl !== undefined && sectionsUrl !== null) {
      formData.append('sections_url', String(sectionsUrl));
    }

    return formData;
  }

  window.FlyProductFormContract = {
    buildItems,
    appendSectionParams,
  };
})(window);
